import React, { useEffect, useState } from 'react';
import { Save, Layout } from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';

// All public pages with PageHero. Each page maps to 4 settings keys:
//   page_<slug>_kicker, page_<slug>_title, page_<slug>_subtitle, page_<slug>_text
const PAGES = [
  { slug: 'about', label: 'Kurumsal / Hakkımızda', path: '/kurumsal' },
  { slug: 'presidents', label: 'Başkanlarımız', path: '/kurumsal/baskanlar' },
  { slug: 'boards', label: 'Kurullarımız', path: '/kurumsal/kurullar' },
  { slug: 'units', label: 'Eğitim Destek Birimleri', path: '/kurumsal/birimler' },
  { slug: 'media', label: 'Medyada YEKÜV', path: '/kurumsal/medya' },
  { slug: 'publications', label: 'Yayınlarımız', path: '/kurumsal/yayinlar' },
  { slug: 'school', label: 'Nilüfer Gökay Okulu', path: '/kurumsal/okul' },
  { slug: 'sponsors', label: 'Destekçilerimiz', path: '/kurumsal/destekciler' },
  { slug: 'tax', label: 'Vergi Muafiyeti', path: '/kurumsal/vergi' },
  { slug: 'kvkk', label: 'KVKK', path: '/kurumsal/kvkk' },
  { slug: 'donate', label: 'Bağış Yap', path: '/bagis' },
  { slug: 'donation_types', label: 'Bağış Türleri', path: '/bagis/turler' },
  { slug: 'special_day', label: 'Özel Gün Bağışı', path: '/bagis/ozel-gun' },
  { slug: 'donation_channels', label: 'Bağış Kanalları', path: '/bagis/kanallar' },
  { slug: 'scholarships', label: 'Burs Başvuru', path: '/burslar' },
  { slug: 'volunteer', label: 'Gönüllü Ol', path: '/gonullu' },
  { slug: 'contact', label: 'İletişim', path: '/iletisim' },
  { slug: 'projects', label: 'Projeler & Etkinlikler', path: '/projeler' },
  { slug: 'news', label: 'Duyurular', path: '/duyurular' },
];

export default function AdminPageTexts() {
  const [values, setValues] = useState({});
  const [active, setActive] = useState(PAGES[0].slug);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.adminListSettings().then(list => {
      const m = {};
      list.forEach(s => { m[s.key] = s.value; });
      setValues(m);
    });
  }, []);

  const k = (slug, field) => `page_${slug}_${field}`;
  const cur = PAGES.find(p => p.slug === active);

  const fields = [
    { f: 'kicker', label: 'Üst Etiket (kicker)', placeholder: 'Örn: Destekçilerimiz', long: false },
    { f: 'title', label: 'Ana Başlık (HTML kabul eder)', placeholder: 'Birlikte daha güçlü.', long: true },
    { f: 'subtitle', label: 'Alt Başlık / Vurgu', placeholder: '(opsiyonel) öncül başlık', long: false },
    { f: 'text', label: 'Açıklama Paragrafı', placeholder: 'Sayfa hero açıklaması...', long: true },
  ];

  const saveAll = async () => {
    setSaving(true);
    try {
      for (const fd of fields) {
        // eslint-disable-next-line no-await-in-loop
        await api.adminUpsertSetting(k(active, fd.f), values[k(active, fd.f)] || '');
      }
      toast.success(`"${cur.label}" sayfa metinleri kaydedildi`);
    } catch {
      toast.error('Kaydedilemedi');
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-page-texts">
      <div className="mb-8">
        <div className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-bold">İçerik</div>
        <h1 className="font-display text-4xl mt-2 leading-none">Sayfa Metinleri</h1>
        <p className="mt-2 text-ink-400 text-sm">Her public sayfanın hero başlığı, açıklaması ve etiketleri buradan düzenlenir. Boş bırakılırsa varsayılan metin kullanılır.</p>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        {/* Sidebar — page list */}
        <div className="bg-white rounded-2xl border border-ink-100 p-3 self-start max-h-[80vh] overflow-y-auto">
          <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-ink-400 font-bold">Sayfalar</div>
          <div className="space-y-0.5">
            {PAGES.map(p => (
              <button key={p.slug} onClick={() => setActive(p.slug)} data-testid={`page-${p.slug}`}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${active === p.slug ? 'bg-ink-600 text-cream' : 'text-ink-500 hover:bg-cream-200'}`}>
                <div className="font-semibold flex items-center gap-2"><Layout className="w-3.5 h-3.5 opacity-60" /> {p.label}</div>
                <div className={`text-[10px] mt-0.5 font-mono ${active === p.slug ? 'text-cream/60' : 'text-ink-400'}`}>{p.path}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Editor */}
        <div className="bg-white rounded-2xl border border-ink-100 p-6 lg:p-8">
          <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-coral-300 font-bold">{cur.label}</div>
              <h2 className="font-display text-2xl mt-1">{cur.path}</h2>
              <a href={cur.path} target="_blank" rel="noopener noreferrer" className="text-xs text-ink-400 hover:text-ink-600 mt-1 inline-block">→ Sayfayı yeni sekmede aç</a>
            </div>
            <button onClick={saveAll} disabled={saving} data-testid="page-texts-save"
              className="bg-ink-600 text-cream rounded-full px-6 h-11 text-sm font-semibold hover:bg-ink-500 inline-flex items-center gap-2 disabled:opacity-60">
              <Save className="w-4 h-4" /> {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </div>

          <div className="grid gap-4">
            {fields.map(fd => {
              const key = k(active, fd.f);
              return (
                <div key={fd.f}>
                  <label className="text-[10px] tracking-[0.25em] uppercase text-ink-400 font-bold">{fd.label}</label>
                  <div className="mt-1.5">
                    {fd.long ? (
                      <textarea
                        value={values[key] || ''}
                        onChange={e => setValues(s => ({ ...s, [key]: e.target.value }))}
                        rows={fd.f === 'title' ? 2 : 4}
                        placeholder={fd.placeholder}
                        data-testid={`field-${fd.f}`}
                        className="w-full bg-cream-200 border border-ink-100 focus:border-ink-500 rounded-xl px-3 py-2.5 text-sm outline-none resize-none"
                      />
                    ) : (
                      <input
                        value={values[key] || ''}
                        onChange={e => setValues(s => ({ ...s, [key]: e.target.value }))}
                        placeholder={fd.placeholder}
                        data-testid={`field-${fd.f}`}
                        className="w-full bg-cream-200 border border-ink-100 focus:border-ink-500 rounded-xl px-3 h-11 text-sm outline-none"
                      />
                    )}
                  </div>
                  <div className="mt-1 text-[10px] text-ink-400 font-mono">{key}</div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 p-4 rounded-xl bg-gold/10 border border-gold/30 text-xs text-ink-600">
            <strong>İpucu:</strong> "Ana Başlık" alanında HTML kullanabilirsiniz. Örnek: <code className="bg-white px-1.5 py-0.5 rounded">Birlikte &lt;em&gt;daha güçlü&lt;/em&gt;</code> → vurgulu kısım italik gösterilir.
          </div>
        </div>
      </div>
    </div>
  );
}
